/**
 * Version selector dropdown for switching between label versions
 *
 * Supports 3-table architecture (Dec 2025):
 * - FDA current: Current FDA version from drugs table
 * - FDA historical: Old FDA versions from historical_versions table
 * - Draft: User-uploaded drafts from label_versions table
 */
import React from 'react';
import type {
  ApprovalLetterSummary, VersionSummary, UnifiedVersionSummary, VersionSource,
  SBASummaryForDrug, SBAReviewCycle, UnmatchedLabel,
} from '../types';
import type { FDALetterSummary } from '../types/fdaLetter';
import {
  formatRegionalVersionLabel,
  labelPrefixForRegion,
} from '../utils/labelVersionFormat';
import { resolveSbaApplicationType } from '../utils/sbaApplicationType';
import SplitDropdown, { SplitDropdownGroup } from './SplitDropdown';
import {
  buildLabelOptions, buildDocGroups, defaultLabelId, docIdForLabel,
  attachedLabelId, attachedLabelIdForDoc, canonicalDocId,
  firstDocIdForSubmission, submissionLabelOf, LabelOption, DocOption,
  buildLetterGroups, letterOfCycle, LetterOption,
} from '../utils/submissionSplitModel';

/**
 * Sizing contract for the three split-dropdown columns (Label / HA Review /
 * Approval Letter). These are the flex items of the `flex-wrap` row, so the
 * row's breakpoint is arithmetic, not a media query:
 *
 *   floor  156px each (120px face + 36px caret) -> row 492px
 *   above  no ceiling: the three DIVIDE whatever their row is given
 *
 * So, for a row of width W: three across at (W-24)/3 each from ~492px up, 2+1
 * below ~492px, one per line below ~324px. The predecessor pinned each face at
 * `w-48` with no shrink, which made 708px a cliff — one pixel under it and it
 * broke apart. `min-w` is what keeps a squeezed face readable.
 * `SplitDropdown` itself is `w-full`, so it inherits whatever this column gets.
 *
 * THERE IS DELIBERATELY NO `max-w` HERE ANY MORE. An earlier revision capped
 * each column at 206px to stop them sprawling on a wide monitor; once the
 * controls moved to their own full-width header row that cap became the visible
 * defect instead — the panel stretched while its contents stayed bunched
 * against the left edge with dead space beside them. The ceiling now lives only
 * on `SPLIT_COL_CAPPED_CLASS` below, for columns that sit OUTSIDE the panel.
 * The trade is real and intentional: on a very wide monitor these columns get
 * correspondingly wide (~450px each at 1900px of row). If that ever reads as
 * sprawl, the fix is a generous cap here — not a return to 206px.
 *
 * WHY `basis` IS THE FLOOR (156px) AND NOT THE CEILING — this is the whole
 * trick, and it is easy to get backwards. A `flex-wrap` container collects
 * items onto a line using each item's OUTER HYPOTHETICAL MAIN SIZE — the flex
 * base size clamped by min/max width (flexbox §9.3) — and only then resolves
 * flexible lengths on the line it settled (§9.7). A 206px basis therefore
 * hypothesises at 206px and wraps the third column the instant 3x206+gaps stops
 * fitting; it never gets the chance to narrow, so you have moved the cliff from
 * 708px to 642px rather than removed it. The first cut of this change did
 * exactly that. Basing at the floor hypothesises at 156px, so all three claim
 * one line down to ~492px. (Note what makes them *look* like they shrink in the
 * 492-642 band: `grow` distributing progressively less free space, not
 * `flex-shrink` running. With the explicit 156px `min-w`, any basis at or below
 * the floor behaves identically — the basis is written as 156px to say so out
 * loud.) Verified in a headless Chrome render against the compiled CSS, not
 * inferred: jsdom computes no layout, so the tests below CANNOT catch this.
 *
 * Written as `grow basis-[...]` rather than `flex-1 basis-[...]` on purpose: the
 * `flex-1` shorthand also sets `flex-basis:0%`, so which one wins depends on
 * Tailwind's utility emission order — a silent, build-order-dependent trap.
 */
const SPLIT_COL_CLASS = 'flex flex-col gap-1 grow basis-[156px] min-w-[156px]';

/**
 * The same contract WITH the 206px ceiling, for a column that stands outside
 * the submission panel (Enforcement). Inside the panel the columns are
 * deliberately uncapped so they divide the panel's width between them — the
 * panel is full-width now that it owns a header row, and a cap there just left
 * the controls bunched at the left with dead space to their right. Out here the
 * cap is what stops a lone sibling column from taking a share it has no use
 * for (uncapped, it resolves to ~296px on a 947px row): when a
 * flex item hits its max-width it freezes and the free space redistributes to
 * the others (flexbox §9.7), which is precisely how the panel ends up with
 * everything Enforcement does not need.
 */
const SPLIT_COL_CAPPED_CLASS = `${SPLIT_COL_CLASS} max-w-[206px]`;

interface VersionSelectorProps {
  // Legacy: draft versions only (from fetchVersionHistory)
  versions: VersionSummary[];
  selectedVersionId: number | null;
  onVersionChange: (versionId: number | null) => void;
  isLoading?: boolean;
  showFdaOption?: boolean;
  // NEW: Unified versions from 3-table architecture (from getAllVersions)
  unifiedVersions?: UnifiedVersionSummary[];
  // NEW: Callback for selecting a version with its source type
  onUnifiedVersionChange?: (source: VersionSource, id: number) => void;
  // NEW: Track selected FDA historical version ID
  selectedHistoricalId?: number | null;
  // Drug region (US | UK | AU | EU | CA | …).  Used to render the
  // regional label name for FDA-current and FDA-historical entries
  // via ``labelPrefixForRegion`` (USPI / EU SmPC / UK SmPC /
  // CA Product Monograph / AU PI).
  drugRegion?: string | null;
  // SBA (FDA Review) documents linked to this drug
  sbaDocuments?: SBASummaryForDrug[];
  /**
   * Classic-only view-model entry for an exact search-result document that
   * the latest-per-type bundle projection omitted.
   */
  pinnedSbaDocument?: SBASummaryForDrug | null;
  selectedSbaId?: number | null;
  reviewLetters?: ApprovalLetterSummary[];
  selectedApprovalLetterId?: number | null;
  // EPAR (EMA Review) documents linked to this drug
  // Reuses SBASummaryForDrug because the backend response shape is
  // identical (epar_routes.list_epar_documents was cloned from
  // sba_routes.list_sba_documents).
  eparDocuments?: SBASummaryForDrug[];
  selectedEparId?: number | null;
  // UK MHRA PIL PDFs linked to this drug.  Reuses SBASummaryForDrug
  // because the dropdown only reads the shared fields (id, trade_name,
  // doc_type, doc_type_label, ingestion_status).  PIL-specific fields
  // (pmid, pil_version_str, parse_method) ride along in the response
  // and are accessed by the viewer via type-narrowing.  Region='UK'
  // drugs only — DrugDetailPage fetches an empty list for non-UK.
  ukPilDocuments?: SBASummaryForDrug[];
  selectedUkPilId?: number | null;
  // CA Health Canada PM (Product Monograph) PDFs linked to this drug.
  // Reuses SBASummaryForDrug because the dropdown only reads the shared
  // fields (id, trade_name, doc_type, doc_type_label, ingestion_status).
  // PM-specific fields (pm_code, pm_version_str, pdf_page_count,
  // total_sections) ride along in the response and are accessed by the
  // viewer via type-narrowing.  Region='CA' drugs only — DrugDetailPage
  // fetches an empty list for non-CA.
  caPmDocuments?: SBASummaryForDrug[];
  selectedCaPmId?: number | null;
  // AU TGA (PI / CMI) documents linked to this drug.  Reuses
  // SBASummaryForDrug because the dropdown only reads the shared
  // fields (id, trade_name, doc_type, doc_type_label,
  // ingestion_status).  AU is a two-doc-type family — each document's
  // `doc_type` is "pi" or "cmi"; the `doc_type_label` from the
  // backend carries the per-type display string.  Region='AU' drugs
  // only — DrugDetailPage fetches an empty list for non-AU.
  auDocuments?: SBASummaryForDrug[];
  selectedAuId?: number | null;
  // EU EMA SmPC documents linked to this drug.  Reuses
  // SBASummaryForDrug (shared fields only).  EU is a single-doc-type
  // family ("smpc"); `doc_type_label` carries "EU SmPC".  Region='EU'
  // drugs only — DrugDetailPage fetches an empty list for non-EU.
  euDocuments?: SBASummaryForDrug[];
  selectedEuId?: number | null;
  // JP PMDA supplementary PDFs (patient_leaflet / interview_form /
  // review_report / rmp) linked to this drug.  Reuses SBASummaryForDrug
  // (shared fields only); `doc_type_label` carries the per-type display
  // string.  Region='JP' drugs only.
  jpPdfDocuments?: SBASummaryForDrug[];
  selectedJpPdfId?: number | null;
  // CN NMPA supplementary PDFs (admin-only) — same shape/handling as
  // ``jpPdfDocuments``.  DrugDetailPage passes an empty list to non-admins so
  // the group is never offered.  Region='CN' drugs only.
  zhPdfDocuments?: SBASummaryForDrug[];
  selectedZhPdfId?: number | null;
  /** Swissmedic label PDFs (ch_pdf, admin-only canary). */
  chPdfDocuments?: SBASummaryForDrug[];
  selectedChPdfId?: number | null;
  /** BfArM label PDFs (de_pdf, admin-only canary): current SmPC + PIL. */
  dePdfDocuments?: (SBASummaryForDrug & {
    version_str?: string | null; family?: string; part_title?: string | null })[];
  selectedDePdfId?: number | null;
  /** US Drugs@FDA label PDFs (fda_label_pdf, admin-only): EVERY approved
   *  labeling action, not just the current one — that is the family's point. */
  fdaLabelPdfDocuments?: (SBASummaryForDrug & {
    version_str?: string | null; action_date?: string | null })[];
  selectedFdaLabelPdfId?: number | null;
  /** ANSM label documents (fr_html, admin-only canary): current RCP + Notice. */
  frHtmlDocuments?: { id: number; doc_type: string; doc_type_label: string }[];
  selectedFrHtmlId?: number | null;
  // FDA Letters linked to this drug
  fdaLetters?: FDALetterSummary[];
  selectedFdaLetterId?: number | null;
  // Review-cycle envelope (Task 6/7, flag-gated). Present only when
  // the backend's `/drugs/{drug_id}/sbas` response carried
  // `available_cycles` (`SBA_CYCLE_VIEW_ENABLED` on) — `null`/absent
  // means the flag is off (or the endpoint was called without
  // `?cycle=`), in which case NO cycle selector is rendered and
  // `sbaDocuments` renders exactly as it does today.
  sbaCycleInfo?: {
    selectedCycle: SBAReviewCycle | null;
    availableCycles: SBAReviewCycle[];
    unclassifiedCount: number;
    unclassifiedLetterCount: number;
    // Task A3/B1: label versions `roll_up_labels` did NOT place onto
    // any submission, categorized (`BLANK_APP`/`NO_CATALOG_APP` loud,
    // `NO_QUALIFYING_SUBMISSION` quiet — see `UnmatchedLabel`). Folded
    // into the Label split-dropdown by `buildLabelOptions` (shown as
    // "no supplement", loud reasons get a ⚠) so no label is ever
    // stranded.
    unmatchedLabels?: UnmatchedLabel[];
  } | null;
  // Fires with the raw `?cycle=` value to send on the refetch —
  // either an `fda_submission_id` (as a string) or the literal
  // `'unclassified'`.
  onSbaCycleChange?: (cycleParam: string) => void;
  // The drug's own `drugs.id` (Task B1/B4). Needed by `labelDispatch`
  // so the CURRENT label — whether it appears as a cycle chip or in
  // the unmatched surface — routes to the canonical
  // `('fda_current', drugId)` view rather than `fda_historical`.
  drugId: number;
  // FDA Submission View (Beta) opt-in — replaces the removed build-time
  // flag. `submissionViewEligible` is the
  // drug being US-region; when false the toggle is not rendered at all (no
  // dead control on EU/UK/CA/AU/JP pages). The button LABEL follows the
  // committed response (`sbaCycleInfo != null`), never the stored preference,
  // so an unavailable opt-in retries into submission view instead of
  // silently flipping the user's choice off.
  submissionViewEligible?: boolean;
  // `transition-error` = a user-initiated view switch failed, so the
  // transition-specific copy is correct. `load-error` = a mount-time load
  // failed (possibly for a user who never opted in), so the copy must stay
  // mode-neutral and never claim a switch was attempted.
  sbaListStatus?: 'idle' | 'loading' | 'unavailable' | 'transition-error' | 'load-error';
  // Route-level gate owned by the page: true while the drug on screen is not
  // yet the drug the route asks for (metadata still in flight). The toggle
  // acts on the DISPLAYED drug, so during that window a click is dead — it
  // cannot change what the user is looking at, but it would still write the
  // preference. Kept separate from `sbaListStatus === 'loading'`: that one
  // means "this drug's list is being switched" and owns the spinner.
  submissionViewToggleDisabled?: boolean;
  onSubmissionViewToggle?: () => void;
}

/**
 * Build the dropdown text for a single unified version row.  FDA-
 * current and FDA-historical entries use the regional naming
 * convention (e.g. "USPI - V27 - Jan 02 2025" / "UK SmPC - V3 -
 * May 08 2026") via :func:`formatRegionalVersionLabel`; drafts retain
 * their own "Draft v3 (status)" format because they live in user
 * upload space, not regulator territory.
 */
/**
 * Inline role-badge suffix for FDA letter ``<option>`` labels.
 *
 * FDA letters render inside a ``<select>``, so a styled ``<span>``
 * badge isn't allowed (option children are text-only).  The role
 * surfaces as a parenthesised suffix so the user can still see why a
 * letter is in the list — "Target" for the letter's primary subject,
 * "Comparator" for approval-phrasing references, "Mentioned" for
 * looser body mentions.  Target is omitted because it's the
 * historical default — only the new comparator/mentioned categories
 * need the visual cue.
 */
const fdaLetterRoleSuffix = (role: string | undefined): string => {
  if (role === 'comparator') return ' (Comparator)';
  if (role === 'mentioned') return ' (Mentioned)';
  return '';
};

/**
 * User-facing name for an FDA enforcement letter's type. Single source of
 * the copy: the flat `<select>` optgroups and the Enforcement split-
 * dropdown must never drift apart on what a `warning` letter is called.
 */
const fdaLetterTypeLabel = (letterType: FDALetterSummary['letter_type']): string => {
  if (letterType === 'promotional') return 'Promotional Material';
  if (letterType === 'warning') return 'FDA Warning Letter';
  return 'FDA Untitled Letter';
};

/**
 * Role as a styled badge — the split-dropdown equivalent of
 * `fdaLetterRoleSuffix`. That helper is a *text* suffix only because
 * `<option>` children cannot hold an element; a `SplitDropdownItem`
 * takes a ReactNode, so here the role can be a real badge. Same
 * semantics: `target` is the historical default and stays unbadged, so
 * only the comparator/mentioned cases get a visual cue.
 */
const fdaLetterRoleBadge = (role: FDALetterSummary['role']): React.ReactNode => {
  if (role === 'comparator') {
    return <span className="rounded bg-sky-100 px-1.5 text-[9px] font-bold uppercase text-sky-700">Comparator</span>;
  }
  if (role === 'mentioned') {
    return <span className="rounded bg-slate-100 px-1.5 text-[9px] font-bold uppercase text-slate-500">Mentioned</span>;
  }
  return null;
};

/**
 * Enforcement letters newest-first, undated ones last (`issued_date` is
 * nullable). `id` desc breaks date ties so the order is stable rather
 * than dependent on the backend's row order. Returns a new array — the
 * `fdaLetters` prop is never mutated.
 */
const sortEnforcementLetters = (letters: FDALetterSummary[]): FDALetterSummary[] =>
  [...letters].sort((a, b) => {
    if (a.issued_date !== b.issued_date) {
      if (!a.issued_date) return 1;
      if (!b.issued_date) return -1;
      return a.issued_date < b.issued_date ? 1 : -1;
    }
    return b.id - a.id;
  });

/**
 * Parenthesised suffix flagging an SBA doc whose filename referenced
 * more than one submission number (`is_multi_submission`, Task 3/6 —
 * e.g. `021187Orig1s021s022SumR.pdf`). Same text-only-suffix pattern
 * as `fdaLetterRoleSuffix` above — `<option>` children can't hold a
 * styled `<span>` badge. `undefined` (flag off / flat legacy shape,
 * field absent) renders no suffix.
 */
const multiSubmissionSuffix = (isMulti: boolean | undefined): string =>
  isMulti ? ' (multi-submission)' : '';

const getVersionLabel = (
  version: UnifiedVersionSummary,
  region?: string | null,
): string => {
  const { source, version_number, status } = version;

  switch (source) {
    case 'fda_current':
    case 'fda_historical':
      return formatRegionalVersionLabel(region, version.effective_date, version_number);
    case 'draft':
      return `Draft v${version_number}${status && status !== 'published' ? ` (${status})` : ''}`;
    default:
      return `v${version_number}`;
  }
};

const VersionSelector: React.FC<VersionSelectorProps> = ({
  versions,
  selectedVersionId,
  onVersionChange,
  isLoading = false,
  showFdaOption = true,
  unifiedVersions,
  onUnifiedVersionChange,
  selectedHistoricalId,
  drugRegion,
  sbaDocuments,
  pinnedSbaDocument,
  selectedSbaId,
  reviewLetters,
  selectedApprovalLetterId,
  eparDocuments,
  selectedEparId,
  ukPilDocuments,
  selectedUkPilId,
  caPmDocuments,
  selectedCaPmId,
  auDocuments,
  selectedAuId,
  euDocuments,
  selectedEuId,
  jpPdfDocuments,
  selectedJpPdfId,
  zhPdfDocuments,
  selectedZhPdfId,
  chPdfDocuments,
  selectedChPdfId,
  dePdfDocuments,
  selectedDePdfId,
  fdaLabelPdfDocuments,
  selectedFdaLabelPdfId,
  frHtmlDocuments,
  selectedFrHtmlId,
  fdaLetters,
  selectedFdaLetterId,
  sbaCycleInfo,
  // NOTE: `onSbaCycleChange` is intentionally NOT destructured here —
  // the cycle `<select>` that called it (`renderSbaCycleSelector`) was
  // removed in favor of the client-side-linked split-dropdowns, which
  // never refetch per-selection. The prop stays in `VersionSelectorProps`
  // (Task 4 retires it from the `DrugDetailPage` call site).
  drugId,
  submissionViewEligible,
  sbaListStatus,
  submissionViewToggleDisabled,
  onSubmissionViewToggle,
}) => {
  const classicSbaDocuments = React.useMemo(() => {
    const documents = sbaDocuments ?? [];
    if (
      !pinnedSbaDocument
      || pinnedSbaDocument.id !== selectedSbaId
      || documents.some(document => document.id === pinnedSbaDocument.id)
    ) {
      return documents;
    }
    return [...documents, pinnedSbaDocument];
  }, [pinnedSbaDocument, sbaDocuments, selectedSbaId]);

  const isPinnedSbaDocument = (documentId: number): boolean =>
    pinnedSbaDocument?.id === documentId
    && selectedSbaId === documentId
    && !(sbaDocuments ?? []).some(document => document.id === documentId);

  // Shared dispatch for any label chip regardless of which surface it
  // lives in (cycle card, unmatched surface, or — B4 — Compare): the
  // CURRENT label always routes to the canonical
  // `('fda_current', drugId)` view; every other label routes to
  // `('fda_historical', drug_version_id)`, same as the existing
  // historical-viewer wiring. Defined here (B1) so B2/B4 can reuse it.
  const labelDispatch = (lv: { drug_version_id: number; is_current: boolean }) => {
    if (!onUnifiedVersionChange) return;
    if (lv.is_current) {
      onUnifiedVersionChange('fda_current', drugId);
    } else {
      onUnifiedVersionChange('fda_historical', lv.drug_version_id);
    }
  };

  // Linked split-dropdowns (submission-split design, Task 3). All
  // linking client-side — the `?cycle=all` envelope already carries
  // every doc + cycle, so no per-selection refetch.
  const splitLabels = React.useMemo<LabelOption[]>(
    () => (sbaCycleInfo ? buildLabelOptions(sbaCycleInfo.availableCycles, sbaCycleInfo.unmatchedLabels ?? []) : []),
    [sbaCycleInfo],
  );
  const docGroups = React.useMemo(
    () => (sbaCycleInfo ? buildDocGroups(sbaDocuments ?? [], sbaCycleInfo.availableCycles) : []),
    [sbaCycleInfo, sbaDocuments],
  );
  const letterGroups = React.useMemo(
    () => (
      sbaCycleInfo
        ? buildLetterGroups(reviewLetters ?? [], sbaCycleInfo.availableCycles)
        : []
    ),
    [sbaCycleInfo, reviewLetters],
  );
  // FDA ENFORCEMENT letters (warning / untitled / promotional) — a
  // different corpus from the approval letters in `letterGroups` above.
  // These carry NO `fda_submission_id`, so they cannot participate in the
  // submission linking and are NOT keyed off `sbaCycleInfo`.
  const enforcementLetterOptions = React.useMemo(
    () => sortEnforcementLetters(fdaLetters ?? []),
    [fdaLetters],
  );
  // Ties the submission panel's visible caption to its `role="group"` as the
  // accessible name. `useId` rather than a literal so two selectors on one
  // page can never collide on the id.
  const submissionGroupLabelId = React.useId();

  // active = the currently-picked item; faces derive from it.
  type Active =
    | { kind: 'label'; id: number }
    | { kind: 'doc'; id: number }
    | { kind: 'letter'; id: number }
    | null;
  const [active, setActive] = React.useState<Active>(null);

  // Sync the faces to what's actually open: external selection (deep-links,
  // Compare, historical) wins; otherwise default to the current label.
  // Priority: open approval letter > open SBA doc > open historical
  // label > current label. Parent state is mutually exclusive, so at
  // most one external document source should normally be present.
  React.useEffect(() => {
    if (!sbaCycleInfo) { setActive(null); return; }
    if (selectedApprovalLetterId != null) {
      setActive({ kind: 'letter', id: selectedApprovalLetterId });
      return;
    }
    if (selectedSbaId != null) { setActive({ kind: 'doc', id: selectedSbaId }); return; }
    if (selectedHistoricalId != null) { setActive({ kind: 'label', id: selectedHistoricalId }); return; }
    const def = defaultLabelId(splitLabels);
    setActive(def != null ? { kind: 'label', id: def } : null);
  }, [
    sbaCycleInfo, selectedApprovalLetterId, selectedSbaId,
    selectedHistoricalId, splitLabels,
  ]);

  // small helpers for the label sub-line
  const subLabelForSubmissionId = (sid: number) => {
    const c = sbaCycleInfo?.availableCycles.find(x => x.fda_submission_id === sid);
    return c ? `${submissionLabelOf(c)}${c.submission_class_code ? ' · ' + c.submission_class_code : ''}` : '';
  };
  const subLabelPlain = (sid: number) => {
    const c = sbaCycleInfo?.availableCycles.find(x => x.fda_submission_id === sid);
    return c ? submissionLabelOf(c) : `Submission ${sid}`;
  };

  // Linked Label / HA Review / Approval Letter split-
  // dropdowns (replaces the old cycle `<select>` + framed submission
  // card + unmatched-labels surface). Renders null when the cycle
  // envelope is absent — same gate the three removed renderers used —
  // so the flat legacy SBA list below is completely unaffected.
  const renderSubmissionSplitControls = () => {
    if (!sbaCycleInfo) return null;

    const labelById = new Map(splitLabels.map(l => [l.drugVersionId, l]));
    const docById = new Map<number, DocOption>();
    docGroups.forEach(g => g.docs.forEach(d => docById.set(d.id, d)));
    const letterById = new Map<number, LetterOption>();
    letterGroups.forEach(g => g.letters.forEach(letter => letterById.set(letter.id, letter)));
    const firstLetterForSid = (sid: number): LetterOption | null => {
      const cycle = sbaCycleInfo.availableCycles.find(
        item => item.fda_submission_id === sid,
      );
      return cycle ? letterOfCycle(cycle, reviewLetters ?? []) : null;
    };

    // Resolve the three displayed faces from one active item.
    let faceLabel: LabelOption | null = null;
    let faceDoc: DocOption | null = null;
    let faceLetter: LetterOption | null = null;
    if (active?.kind === 'label') {
      faceLabel = labelById.get(active.id) ?? null;
      const linkedDocId = faceLabel ? docIdForLabel(faceLabel, sbaDocuments ?? []) : null;
      faceDoc = linkedDocId != null ? docById.get(linkedDocId) ?? null : null;
      faceLetter = faceLabel?.fdaSubmissionId != null
        ? firstLetterForSid(faceLabel.fdaSubmissionId)
        : null;
    } else if (active?.kind === 'doc') {
      // active.id may be a RAW slice/revision id (e.g. a ?source=sba deep link
      // to a size-split part) that dedupe removed from the dropdown — map it to
      // the canonical entry so the face + linked label resolve instead of
      // showing "No HA reviews / No label" while that doc is open.
      // Prefer an exact dropdown row (notably, every unclassified document is
      // retained). Only fall back to the classified-doc canonical id when
      // dedupe actually removed the raw slice/revision from docGroups.
      faceDoc = docById.get(active.id) ?? null;
      if (!faceDoc) {
        const canonId = canonicalDocId(active.id, sbaDocuments ?? []) ?? active.id;
        faceDoc = docById.get(canonId) ?? null;
      }
      const linkedLabelId = faceDoc ? attachedLabelIdForDoc(faceDoc, sbaCycleInfo.availableCycles) : null;
      faceLabel = linkedLabelId != null ? labelById.get(linkedLabelId) ?? null : null;
      faceLetter = faceDoc?.fdaSubmissionId != null
        ? firstLetterForSid(faceDoc.fdaSubmissionId)
        : null;
    } else if (active?.kind === 'letter') {
      // Letter ids are already one-row-per-archived-PDF identities; do
      // not pass them through SBA's canonicalDocId deduplication.
      faceLetter = letterById.get(active.id) ?? null;
      const sid = faceLetter?.fdaSubmissionId ?? null;
      if (sid != null) {
        const cycle = sbaCycleInfo.availableCycles.find(
          item => item.fda_submission_id === sid,
        );
        const labelId = cycle ? attachedLabelId(cycle) : null;
        faceLabel = labelId != null ? labelById.get(labelId) ?? null : null;
        const docId = firstDocIdForSubmission(sid, sbaDocuments ?? []);
        faceDoc = docId != null ? docById.get(docId) ?? null : null;
      }
    }

    // Dates arrive as ISO (label effective_time is a datetime → `…T00:00:00`);
    // the time is always midnight and noise, so show the date only.
    const fmt = (d: string | null) => (d ? d.slice(0, 10) : '');
    const labelSubmissionSid = faceLabel?.fdaSubmissionId ?? null;

    // Label dropdown groups (flat, single group, no header)
    const labelGroups: SplitDropdownGroup[] = [{
      key: 'labels',
      items: splitLabels.map(l => ({
        id: `L${l.drugVersionId}`,
        selected: faceLabel?.drugVersionId === l.drugVersionId,
        primary: (
          <span className="flex items-center gap-2">
            <span>v{l.versionNumber}</span>
            {l.isCurrent && <span className="rounded bg-emerald-100 px-1.5 text-[9px] font-bold uppercase text-emerald-700">Current</span>}
            <span className="text-[11px] text-slate-400">{fmt(l.effectiveDate)}</span>
          </span>
        ),
        secondary: l.fdaSubmissionId != null
          ? subLabelForSubmissionId(l.fdaSubmissionId)
          : (<span className="flex items-center gap-1">
              {(l.unmatchedReason === 'BLANK_APP' || l.unmatchedReason === 'NO_CATALOG_APP') && (
                <span title="Label expected an FDA application but none resolved" className="text-amber-600">⚠</span>
              )}
              <span className="rounded border border-dashed border-slate-300 px-1 text-slate-400">no supplement</span>
            </span>),
      })),
    }];

    const docSplitGroups: SplitDropdownGroup[] = docGroups.map(g => ({
      key: g.key,
      // Unclassified group (no submission) shows the envelope count, per spec;
      // classified groups join their non-empty parts so a null class/date never
      // leaves a dangling "· " separator.
      header: g.fdaSubmissionId == null
        ? `Unclassified (${sbaCycleInfo.unclassifiedCount})`
        : [fmt(g.headerDate), g.submissionClass, g.submissionLabel].filter(Boolean).join(' · '),
      items: g.docs.map(d => ({
        id: `D${d.id}`,
        selected: faceDoc?.id === d.id,
        primary: d.label,
        secondary: fmt(d.date),
      })),
    }));
    const letterSplitGroups: SplitDropdownGroup[] = letterGroups.map(group => ({
      key: group.key,
      header: group.fdaSubmissionId == null
        ? `Unclassified (${sbaCycleInfo.unclassifiedLetterCount})`
        : [
            fmt(group.headerDate),
            group.submissionClass,
            group.submissionLabel,
          ].filter(Boolean).join(' · '),
      items: group.letters.map(letter => ({
        id: `R${letter.id}`,
        selected: faceLetter?.id === letter.id,
        primary: 'Approval Letter',
        secondary: fmt(letter.date),
      })),
    }));

    const onLabelSelect = (itemId: string) => {
      const id = Number(itemId.slice(1));
      const l = labelById.get(id);
      if (!l) return;
      setActive({ kind: 'label', id });
      onVersionChange(null);
      labelDispatch({ drug_version_id: l.drugVersionId, is_current: l.isCurrent });
    };
    const onDocSelect = (itemId: string) => {
      const id = Number(itemId.slice(1));
      setActive({ kind: 'doc', id });
      onVersionChange(null);
      onUnifiedVersionChange && onUnifiedVersionChange('sba', id);
    };
    const onLetterSelect = (itemId: string) => {
      const id = Number(itemId.slice(1));
      if (!letterById.has(id)) return;
      setActive({ kind: 'letter', id });
      onVersionChange(null);
      onUnifiedVersionChange?.('approval-letter', id);
    };

    // Enforcement letters: no submission, therefore no linking and no entry
    // into `active` (which exists purely to keep the three linked faces in
    // step). The face reads `selectedFdaLetterId` — the parent's own record of
    // what is open.
    //
    // This does NOT mean the three linked faces survive an enforcement
    // selection, and an earlier version of this comment wrongly claimed it
    // did. `handleUnifiedVersionChange('fda-letter', …)` runs the page's
    // `clearAll()` like all 13 document sources do, nulling
    // selectedHistoricalId / selectedSbaId / selectedApprovalLetterId; the
    // sync effect above then legitimately falls through to the default label.
    // So opening an enforcement letter DOES reset the three faces to default.
    // Accepted as correct by the user (2026-08-17): with an enforcement letter
    // open, no submission document is open, so the default is the honest face.
    // Pinned by the rerender test in VersionSelector.submissionSplit.test.tsx —
    // do not "fix" this reset without revisiting that decision.
    const faceEnforcement = selectedFdaLetterId != null
      ? enforcementLetterOptions.find(l => l.id === selectedFdaLetterId) ?? null
      : null;
    // An id the summary list doesn't carry — a deep link to a letter linked to
    // another drug, or a failed/stale `fdaLetters` fetch. The PAGE treats any
    // non-null id as "the letter viewer is open", so falling back to the
    // ordinary empty face would have the control deny what is on screen.
    const enforcementOrphanId = selectedFdaLetterId != null && !faceEnforcement
      ? selectedFdaLetterId
      : null;
    const enforcementGroups: SplitDropdownGroup[] = [{
      key: 'enforcement',
      items: enforcementLetterOptions.map(l => ({
        id: `E${l.id}`,
        selected: faceEnforcement?.id === l.id,
        primary: (
          <span className="flex items-center gap-2">
            <span>{fdaLetterTypeLabel(l.letter_type)}</span>
            {fdaLetterRoleBadge(l.role)}
          </span>
        ),
        secondary: fmt(l.issued_date) || 'date unavailable',
      })),
    }];
    const onEnforcementSelect = (itemId: string) => {
      const id = Number(itemId.slice(1));
      if (!enforcementLetterOptions.some(l => l.id === id)) return;
      onVersionChange(null);
      onUnifiedVersionChange?.('fda-letter', id);
    };

    const linkChip = (() => {
      if (faceLabel && faceLabel.fdaSubmissionId != null) {
        return `Linked via ${subLabelPlain(faceLabel.fdaSubmissionId)}`;
      }
      if (faceDoc && faceDoc.fdaSubmissionId != null) return `Linked via ${subLabelPlain(faceDoc.fdaSubmissionId)}`;
      if (faceLetter && faceLetter.fdaSubmissionId != null) {
        return `Linked via ${subLabelPlain(faceLetter.fdaSubmissionId)}`;
      }
      if (faceLabel) return 'No supplement';
      return '';
    })();
    const linkedSubmissionSid = (
      faceLetter?.fdaSubmissionId
      ?? faceDoc?.fdaSubmissionId
      ?? faceLabel?.fdaSubmissionId
      ?? null
    );

    return (
      // `w-full` claims a full-width line in the parent wrap row (the Task B5
      // pattern documented on that container). Without it this is a shrinkable
      // flex item inside a `min-w-0` parent, so a sibling control — the Beta
      // toggle — steals its width, pushing the row toward the point where its
      // own `flex-wrap` stacks the controls vertically. Full-width keeps the
      // submission panel and the Enforcement column side by side and pushes the
      // toggle/drafts/select to the next line; `SPLIT_COL_CLASS` then governs
      // how the columns shrink before wrapping.
      <div className="mb-1.5 flex w-full flex-wrap items-end gap-3" data-testid="submission-split-controls">
        {/* The three dropdowns that link to ONE ANOTHER through the FDA
            submission are boxed together; the submission itself is never a
            control, so the panel header carries its identity (`linkChip`).
            Enforcement letters are a SIBLING of this panel, never a child —
            they have no `fda_submission_id` and so cannot be linked. Tests
            assert that containment, not just presence.

            `role="group"` + `aria-labelledby` so the grouping is not merely
            visual: a screen-reader user gets the same "these three belong
            together, that fourth one does not" structure a sighted user reads
            off the border.

            `grow` + `min-w-0` keep the panel INSIDE the responsive contract
            `SPLIT_COL_CLASS` defines rather than opting out of it: `grow`
            claims the width the Enforcement column doesn't take, and `min-w-0`
            is what actually permits shrinking below content — without it the
            columns inside could not reach their 156px floor and the row would
            overflow instead of wrapping. */}
        <div
          role="group"
          aria-labelledby={submissionGroupLabelId}
          // `basis-[518px]` for the reason the columns inside base at their
          // floor: a wrap row breaks lines on the hypothetical main size, and
          // with the default `basis:auto` this panel hypothesises at MAX-content
          // — wide enough that Enforcement wrapped to a second line on a 1000px
          // window even though both fit. A small basis lets them share the line;
          // `grow` then hands the panel everything Enforcement's 206px cap
          // leaves behind. `min-w-0` is co-load-bearing here, not just for
          // shrinking: the hypothetical size is the basis clamped by the USED
          // min, so `min-width:auto` would drag it back toward min-content.
          //
          // 518 IS THE FLOOR OF THE FIX, NOT A ROUND NUMBER. The three columns
          // need 3x156 + 2x12 = 492, and this panel's border + `px-3` eat 26,
          // so 518 is the narrowest panel that still fits them. At 500 the
          // panel and Enforcement first shared a line at 500+12+156 = 668px —
          // and at that exact width the panel SHRANK from ~668 to 500, leaving
          // 474 inside, so widening the window flipped the columns from
          // three-across to 2+1 until ~704px. Measured in headless Chrome
          // across 640-795px in 5px steps: with 500, widths 670-700 render two
          // rows; with 518, every width renders one.
          className="flex min-w-0 grow basis-[518px] flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-slate-50/60 px-3 pb-2.5 pt-1.5"
          data-testid="submission-linked-group"
        >
        {/* Panel identity sits beside the controls rather than above them: as
            its own line it cost ~24px of vertical space in a header that is now
            two rows deep. `shrink-0` keeps it legible while the columns absorb
            the width; `pb-1` drops it onto the dropdowns' baseline under
            `items-end`. It still carries `aria-labelledby` for the group.
            Beside is the INTENT, not a guarantee: the panel wraps, so on a
            narrow panel this block takes its own line again — which is the
            graceful outcome, since the alternative is squeezing the columns
            below their floor. Confirmed at 947px in a headless render. */}
        <div className="flex shrink-0 items-center gap-2 pb-1">
          <span id={submissionGroupLabelId} className="text-[10px] font-bold uppercase tracking-wide text-slate-500">FDA Submission</span>
          {linkChip && (
            /* `title` restores the "why" the short copy drops, and disambiguates
               it from the HA Review face's own "no supplement" secondary, which
               means something different: that face has no supplement to hang
               documents off, this chip means the LABEL has no submission. */
            <span
              data-testid="submission-link-chip"
              title={linkChip === 'No supplement' ? 'Label revision only — no FDA supplement of its own' : undefined}
              className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700"
            >{linkChip}</span>
          )}
        </div>
        <div className="flex min-w-0 grow flex-wrap items-end gap-3">
        <div className={SPLIT_COL_CLASS} data-testid="label-split-col">
          <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Label</span>
          <SplitDropdown
            ariaLabel="Label"
            testId="label-split"
            facePrimary={faceLabel ? `v${faceLabel.versionNumber}` : 'No label'}
            faceSecondary={faceLabel ? fmt(faceLabel.effectiveDate) : undefined}
            faceEmpty={!faceLabel}
            groups={labelGroups}
            onFaceClick={() => faceLabel && labelDispatch({ drug_version_id: faceLabel.drugVersionId, is_current: faceLabel.isCurrent })}
            onSelect={onLabelSelect}
          />
        </div>
        {/* ORDER: Label -> Approval Letter -> HA Review. The letter is the
            submission's outcome and the review is the supporting analysis, so
            the letter reads first (product decision 2026-08-17). The
            `data-testid`s stay bound to their own control, NOT to a position —
            a positional selector would silently follow a future reorder. */}
        <div className={SPLIT_COL_CLASS} data-testid="review-letter-split-col">
          <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Approval Letter</span>
          <SplitDropdown
            ariaLabel="Approval Letter"
            testId="review-letter-split"
            facePrimary={
              faceLetter
                ? `Approval Letter, ${fmt(faceLetter.date) || 'date unavailable'}`
                : 'No approval letter'
            }
            faceSecondary={
              faceLetter
                ? (
                    faceLetter.fdaSubmissionId != null
                      ? subLabelPlain(faceLetter.fdaSubmissionId)
                      : 'unclassified'
                  )
                : (
                    linkedSubmissionSid != null
                      ? `for ${subLabelPlain(linkedSubmissionSid)}`
                      : 'no supplement'
                  )
            }
            faceEmpty={!faceLetter}
            groups={letterSplitGroups}
            onFaceClick={() => (
              faceLetter
              && onUnifiedVersionChange?.('approval-letter', faceLetter.id)
            )}
            onSelect={onLetterSelect}
          />
        </div>
        <div className={SPLIT_COL_CLASS} data-testid="ha-doc-split-col">
          {/* `testId` stays `ha-doc-split` on purpose — it is an internal test
              handle, not user-facing copy, and churning it would touch every
              selector for no behavioural gain. `ariaLabel` DOES track the
              visible header: they must match, or the accessible name diverges
              from what a sighted user reads (WCAG label-in-name). */}
          <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">HA Review</span>
          <SplitDropdown
            ariaLabel="HA Review"
            testId="ha-doc-split"
            facePrimary={faceDoc ? faceDoc.label : 'Not yet published'}
            faceDashed={!faceDoc}
            faceSecondary={faceDoc ? fmt(faceDoc.date) : (labelSubmissionSid != null ? `for ${subLabelPlain(labelSubmissionSid)}` : 'no supplement')}
            faceEmpty={!faceDoc}
            groups={docSplitGroups}
            onFaceClick={() => faceDoc && onUnifiedVersionChange && onUnifiedVersionChange('sba', faceDoc.id)}
            onSelect={onDocSelect}
          />
        </div>
        </div>
        </div>
        {/* FDA ENFORCEMENT letters — warning / untitled / promotional, drug-
            level rather than submission-level. Hidden entirely when the drug
            has none (the ~2/3 case) rather than shown as a permanently dead
            control; the flat `<select>` optgroup uses the same `length > 0`
            rule. The `orphan` disjunct is the one exception: an OPEN letter
            must never be hidden, or submission view would show it with no
            control acknowledging it (the optgroup is gated off here too).

            `SPLIT_COL_CLASS` so it shrinks on the same contract as the three
            inside the panel. `pb-2.5` matches the panel's bottom padding so
            this dropdown's baseline lines up with theirs under `items-end`. */}
        {(enforcementLetterOptions.length > 0 || enforcementOrphanId != null) && (
          <div className={`${SPLIT_COL_CAPPED_CLASS} pb-2.5`} data-testid="enforcement-letter-split-col">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Enforcement</span>
            <SplitDropdown
              ariaLabel="Enforcement"
              testId="enforcement-letter-split"
              facePrimary={
                faceEnforcement
                  ? fdaLetterTypeLabel(faceEnforcement.letter_type)
                  : enforcementOrphanId != null
                    ? (
                        <span className="flex items-center gap-1">
                          <span title="This letter is open but is not among this drug's linked letters" className="text-amber-600">⚠</span>
                          <span>Letter #{enforcementOrphanId}</span>
                        </span>
                      )
                    : 'No letter selected'
              }
              faceSecondary={
                faceEnforcement
                  ? (fmt(faceEnforcement.issued_date) || 'date unavailable')
                  : enforcementOrphanId != null
                    ? 'not among this drug’s letters'
                    : `${enforcementLetterOptions.length} available`
              }
              faceEmpty={!faceEnforcement}
              groups={enforcementGroups}
              onFaceClick={() => (
                faceEnforcement
                && onUnifiedVersionChange?.('fda-letter', faceEnforcement.id)
              )}
              onSelect={onEnforcementSelect}
            />
          </div>
        )}
        {/* NOTE (merge of origin/dev @ abcb45b0): dev carried a long comment
            here explaining that the chip is a FOURTH item in this wrap row and
            so shifts the `SPLIT_COL_CLASS` thresholds right by its own width
            plus a gap — including a known cosmetic wart where widening the
            window briefly NARROWS the columns (~200px -> 156px in one step) at
            the width the chip first fits on their line. That reasoning is
            retired rather than lost: the chip now lives in the panel header, so
            it is not in this wrap row at all and the thresholds are no longer
            chip-dependent.

            The SYMPTOM, though, is not chip-specific and did come back once by
            another route — the panel's own basis, which let Enforcement join
            the line at a width where the panel then had too little left for
            three columns. Any item added to this row can reintroduce it. The
            invariant to preserve: every item's basis must be at least what its
            contents need, or joining the line makes the layout worse. See the
            518px arithmetic on the panel above. */}
      </div>
    );
  };

  // FDA Submission View (Beta) opt-in control. Rendered from BOTH the
  // unified and legacy paths, same as `renderSubmissionSplitControls`.
  const renderSubmissionViewToggle = () => {
    if (!submissionViewEligible) return null;
    const isActiveView = !!sbaCycleInfo;
    const busy = sbaListStatus === 'loading';
    // `busy` owns the spinner and `aria-busy` (a switch this drug is actually
    // performing); the route gate only removes the affordance, with no
    // spinner, because nothing is being switched.
    const blocked = busy || !!submissionViewToggleDisabled;
    const statusText =
      sbaListStatus === 'unavailable'
        ? "Submission view isn't available for this drug"
        : sbaListStatus === 'transition-error'
          ? "Couldn't switch views. Try again."
          : sbaListStatus === 'load-error'
            ? "Couldn't load review documents."
            : null;

    return (
      <div className="flex flex-col gap-1">
        <button
          type="button"
          data-testid="submission-view-toggle"
          disabled={blocked}
          aria-busy={busy}
          onClick={() => onSubmissionViewToggle?.()}
          // The opt-in is the call to action of the soft launch, so it carries
          // the visual weight (filled indigo, larger type, shadow). The active
          // state is only an exit, so it stays subdued — drawing the eye to the
          // way OUT of the view we want people to try would be backwards.
          className={
            isActiveView
              ? 'inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60'
              : 'inline-flex items-center gap-2 rounded-md border-2 border-indigo-500 bg-white px-3.5 py-2 text-sm font-semibold text-indigo-700 shadow-sm hover:border-indigo-600 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60'
          }
        >
          {busy && (
            <span
              data-testid="submission-view-spinner"
              aria-hidden="true"
              className={`h-3 w-3 animate-spin rounded-full border-2 ${
                isActiveView
                  ? 'border-slate-300 border-t-indigo-500'
                  : 'border-indigo-200 border-t-indigo-600'
              }`}
            />
          )}
          {isActiveView ? (
            '⟳ Back to classic view'
          ) : (
            // Split only for styling — the rendered text is still exactly
            // `⟳ Switch to FDA Submission View (Beta)` per spec §7. The
            // explicit {' '} is load-bearing: without it the concatenated
            // textContent loses the space before "(Beta)".
            <>
              {'⟳ Switch to FDA Submission View'}{' '}
              <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                (Beta)
              </span>
            </>
          )}
        </button>
        {statusText && (
          <span
            data-testid="submission-view-status"
            role="status"
            aria-live="polite"
            className="text-[11px] text-slate-500"
          >
            {statusText}
          </span>
        )}
      </div>
    );
  };

  // Drafts picker (Task B3, submission-group-selector-v2 plan) — a
  // dedicated dropdown for user-uploaded drafts, extracted OUT of the
  // main unified `<select>`'s "Your Drafts" optgroup (unified path)
  // and out of the bare inline draft `<option>`s (legacy path). Both
  // render paths format their own draft labels (unified reuses
  // `getVersionLabel` + the created_at suffix; legacy keeps its
  // existing "Draft vN (status) - N comments" text) and hand this
  // function a pre-formatted `{id, label}` list, so the picker itself
  // stays render-path-agnostic. Flag-independent by design: drafts are
  // region-agnostic and exist whether or not `sbaCycleInfo` is
  // present, so this is NEVER gated on `sbaCycleInfo` (unlike the main
  // select, which B2+B4 hides entirely once FDA labels move to the
  // submission control — a US drug with only drafts + FDA labels would
  // otherwise lose its drafts entirely). Renders null when there are no
  // drafts at all.
  const renderDraftsPicker = (drafts: Array<{ id: number; label: string }>) => {
    if (drafts.length === 0) return null;
    const selected = drafts.find((d) => d.id === selectedVersionId);

    return (
      <div className="relative mb-1.5">
        <select
          aria-label="Drafts"
          data-testid="drafts-picker"
          value={selected ? `draft-${selected.id}` : ''}
          onChange={(e) => {
            const value = e.target.value;
            if (!value.startsWith('draft-')) return;
            const id = parseInt(value.replace('draft-', ''), 10);
            onVersionChange(id);
            if (onUnifiedVersionChange) {
              onUnifiedVersionChange('draft', id);
            }
          }}
          disabled={isLoading}
          className="appearance-none bg-white border border-slate-200 rounded px-3 py-1 pr-8 text-xs font-medium text-slate-600 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="" disabled>
            Drafts
          </option>
          {drafts.map((d) => (
            <option key={`draft-${d.id}`} value={`draft-${d.id}`}>
              {d.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="h-3.5 w-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    );
  };

  // If unified versions are provided, use the new 3-table architecture display
  if (unifiedVersions && unifiedVersions.length > 0) {
    // Separate versions by source
    const fdaVersions = unifiedVersions.filter(v => v.source === 'fda_current' || v.source === 'fda_historical');
    const draftVersions = unifiedVersions.filter(v => v.source === 'draft');

    // Sort FDA versions by version_number descending (newest first)
    const sortedFdaVersions = [...fdaVersions].sort((a, b) => b.version_number - a.version_number);
    // Sort draft versions by version_number descending (newest first)
    const sortedDraftVersions = [...draftVersions].sort((a, b) => b.version_number - a.version_number);

    // Find the current FDA version
    const currentFdaVersion = sortedFdaVersions.find(v => v.source === 'fda_current');

    // Compute the current value based on what's selected
    const computeValue = (): string => {
      // Approval letters live in the submission split control, never
      // in this legacy flat select. Avoid an orphan value or a false
      // current-label selection while the letter viewer is active.
      if (sbaCycleInfo && selectedApprovalLetterId != null) {
        return '';
      }
      // If an FDA enforcement letter is selected. In submission view the
      // Enforcement split-dropdown owns these and the optgroup below is gated
      // off, so this value then matches no option — deliberately NOT guarded
      // with `sbaCycleInfo ? '' : …`. Such a guard was written and removed: a
      // single-select `<select>` auto-selects its first option (the hidden
      // "none selected" placeholder) whenever the controlled value matches
      // nothing, so '' and an orphan value render IDENTICALLY — measured as
      // `value="" selectedIndex=0` both ways. It was dead code that no test
      // could distinguish. Contrast the approval-letter guard above, which IS
      // load-bearing: without its early return, control falls through to
      // branches that can return a real current-label value and show the wrong
      // selection. This branch returns either way, so it cannot fall through.
      if (selectedFdaLetterId !== null && selectedFdaLetterId !== undefined) {
        return `fda-letter-${selectedFdaLetterId}`;
      }
      // If an SBA document is selected — but only when the review-cycle
      // view (sbaCycleInfo) is OFF. When it's on, EVERY sba doc
      // (classified or unclassified) is reachable via the HA Review
      // split-dropdown (Task 3, `renderSubmissionSplitControls` /
      // `buildDocGroups`), so this branch is skipped and we fall
      // through to the FDA-current/historical/draft/default logic
      // below. The active review is reflected by the HA dropdown's
      // highlighted item instead.
      if (!sbaCycleInfo && selectedSbaId !== null && selectedSbaId !== undefined) {
        return `sba-${selectedSbaId}`;
      }
      // If an EPAR document is selected
      if (selectedEparId !== null && selectedEparId !== undefined) {
        return `epar-${selectedEparId}`;
      }
      // If a UK PIL PDF is selected
      if (selectedUkPilId !== null && selectedUkPilId !== undefined) {
        return `ukpil-${selectedUkPilId}`;
      }
      // If a CA PM PDF is selected
      if (selectedCaPmId !== null && selectedCaPmId !== undefined) {
        return `capm-${selectedCaPmId}`;
      }
      // If an AU TGA (PI / CMI) document is selected
      if (selectedAuId !== null && selectedAuId !== undefined) {
        return `audoc-${selectedAuId}`;
      }
      // If an EU EMA SmPC document is selected
      if (selectedEuId !== null && selectedEuId !== undefined) {
        return `eudoc-${selectedEuId}`;
      }
      // If a JP PMDA supplementary PDF is selected
      if (selectedJpPdfId !== null && selectedJpPdfId !== undefined) {
        return `jppdfdoc-${selectedJpPdfId}`;
      }
      // If a CN NMPA supplementary PDF is selected (admin-only)
      if (selectedZhPdfId !== null && selectedZhPdfId !== undefined) {
        return `zhpdfdoc-${selectedZhPdfId}`;
      }
      // If a Swissmedic label PDF is selected
      if (selectedChPdfId !== null && selectedChPdfId !== undefined) {
        return `chpdfdoc-${selectedChPdfId}`;
      }
      // If a BfArM label PDF is selected
      if (selectedFdaLabelPdfId !== null && selectedFdaLabelPdfId !== undefined) {
        return `fdalabelpdfdoc-${selectedFdaLabelPdfId}`;
      }
      if (selectedDePdfId !== null && selectedDePdfId !== undefined) {
        return `depdfdoc-${selectedDePdfId}`;
      }
      if (selectedFrHtmlId !== null && selectedFrHtmlId !== undefined) {
        return `frhtmldoc-${selectedFrHtmlId}`;
      }
      // NOTE: drafts are no longer an option in this <select> (Task
      // B3 — they moved to their own dedicated picker), so there is
      // deliberately no `draft-${selectedVersionId}` branch here
      // anymore; returning that value would orphan this <select>
      // (point at a removed <option>), the same hazard the
      // `sbaCycleInfo` guards below fix for fda_current/historical.
      // The drafts picker tracks the active draft selection itself.
      // If a historical FDA version is selected — but only when the
      // review-cycle view (sbaCycleInfo) is OFF. When sbaCycleInfo is
      // present, the FDA Versions optgroup below is gated off too
      // (Task B2) — the submission control now owns FDA label
      // navigation, so this value would orphan the <select> (point at
      // a removed <option>). Mirrors the `sba-` guard above.
      if (!sbaCycleInfo && selectedHistoricalId !== null) {
        return `fda_historical-${selectedHistoricalId}`;
      }
      // JP drugs: the XML package insert is the default/primary view;
      // supplementary PDFs are the other dropdown entries.
      if (drugRegion === 'JP') {
        return 'jplabel';
      }
      // Task B3 regression guard: when a DRAFT is the active view
      // (selectedVersionId !== null — the drafts picker owns this
      // selection, see renderDraftsPicker above), this <select> must
      // show nothing selected rather than falling through to the
      // fda_current/fda default below. That default matches a real
      // rendered <option> ("Current Label") and would falsely
      // highlight it while the drafts picker correctly shows the
      // active draft. Applies regardless of `sbaCycleInfo`.
      if (selectedVersionId !== null) {
        return '';
      }
      // Default to current FDA version — same sbaCycleInfo orphan-value
      // guard as above (Task B2).
      if (!sbaCycleInfo) {
        return currentFdaVersion ? `fda_current-${currentFdaVersion.id}` : 'fda';
      }
      return '';
    };

    // Task B2 (submission-group-selector v2 plan): once the submission
    // control owns FDA label navigation (sbaCycleInfo present), the
    // FDA Versions optgroup and the sba- optgroup are both gated off
    // (see below / Task 3 above). If EPAR/UK/CA/AU/EU/JP/letters are
    // ALSO absent, the residual <select> would have zero <option>s —
    // render nothing rather than an empty, non-functional dropdown.
    // Task B3: drafts moved to their own picker (`renderDraftsPicker`)
    // and are therefore intentionally EXCLUDED from this option-
    // presence check — they no longer live in this <select> at all,
    // so their presence must not keep an otherwise-empty main <select>
    // rendered (a US FDA drug with only drafts + FDA labels must hide
    // this residual select, since both its would-be contents moved
    // elsewhere).
    const hasFdaVersionOption = !sbaCycleInfo && showFdaOption;
    const hasSbaOptions = !sbaCycleInfo && classicSbaDocuments.length > 0;
    const hasEparOptions = !!eparDocuments && eparDocuments.length > 0;
    const hasUkPilOptions = !!ukPilDocuments && ukPilDocuments.length > 0;
    const hasCaPmOptions = !!caPmDocuments && caPmDocuments.length > 0;
    const hasAuOptions = !!auDocuments && auDocuments.length > 0;
    const hasEuOptions = !!euDocuments && euDocuments.length > 0;
    const hasJpLabelOption = drugRegion === 'JP';
    const hasJpPdfOptions = !!jpPdfDocuments && jpPdfDocuments.length > 0;
    const hasZhPdfOptions = !!zhPdfDocuments && zhPdfDocuments.length > 0;
    const hasChPdfOptions = !!chPdfDocuments && chPdfDocuments.length > 0;
    const hasDePdfOptions = !!dePdfDocuments && dePdfDocuments.length > 0;
    const hasFdaLabelPdfOptions = !!fdaLabelPdfDocuments && fdaLabelPdfDocuments.length > 0;
    const hasFrHtmlOptions = !!frHtmlDocuments && frHtmlDocuments.length > 0;
    const hasFdaLetterOptions = !sbaCycleInfo && !!fdaLetters && fdaLetters.length > 0;
    const hasAnySelectOption =
      hasFdaVersionOption ||
      hasSbaOptions ||
      hasEparOptions ||
      hasUkPilOptions ||
      hasCaPmOptions ||
      hasAuOptions ||
      hasEuOptions ||
      hasJpLabelOption ||
      hasJpPdfOptions ||
      hasZhPdfOptions ||
      hasChPdfOptions ||
      hasDePdfOptions ||
      hasFdaLabelPdfOptions ||
      hasFrHtmlOptions ||
      hasFdaLetterOptions;

    return (
      // Task B5 (submission-group-selector-v2 plan): flex-wrap lets the
      // compact controls (split-dropdown controls, drafts picker, main
      // select) pack inline while `w-full` wide content forces its own
      // full-width line beneath them — no logic change, layout only.
      <div className="relative flex flex-wrap items-start gap-2">
        {renderSubmissionSplitControls()}
        {renderSubmissionViewToggle()}
        {renderDraftsPicker(
          sortedDraftVersions.map((version) => ({
            id: version.id,
            label:
              getVersionLabel(version, drugRegion) +
              (version.created_at ? ` (${new Date(version.created_at).toLocaleDateString()})` : ''),
          })),
        )}
        {hasAnySelectOption && (
        <div className="relative">
          <select
            value={computeValue()}
            onChange={(e) => {
              const value = e.target.value;
              console.log('[VersionSelector] onChange fired, value:', value);

              if (value === 'fda' || value.startsWith('fda_current-')) {
                // Viewing FDA current
                console.log('[VersionSelector] Selecting FDA current');
                onVersionChange(null);
                if (onUnifiedVersionChange && currentFdaVersion) {
                  onUnifiedVersionChange('fda_current', currentFdaVersion.id);
                }
              } else if (value.startsWith('fda_historical-')) {
                // Viewing FDA historical
                const id = parseInt(value.replace('fda_historical-', ''), 10);
                console.log('[VersionSelector] Selecting FDA historical, id:', id);
                onVersionChange(null); // Clear draft selection
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('fda_historical', id);
                }
              } else if (value.startsWith('fda-letter-')) {
                // Viewing FDA Letter
                const id = parseInt(value.replace('fda-letter-', ''), 10);
                console.log('[VersionSelector] Selecting FDA letter, id:', id);
                onVersionChange(null); // Clear draft selection
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('fda-letter', id);
                }
              } else if (value.startsWith('sba-')) {
                // Viewing SBA (FDA Review)
                const id = parseInt(value.replace('sba-', ''), 10);
                console.log('[VersionSelector] Selecting SBA, id:', id);
                onVersionChange(null); // Clear draft selection
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('sba', id);
                }
              } else if (value.startsWith('epar-')) {
                // Viewing EPAR (EMA Review)
                const id = parseInt(value.replace('epar-', ''), 10);
                console.log('[VersionSelector] Selecting EPAR, id:', id);
                onVersionChange(null); // Clear draft selection
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('epar', id);
                }
              } else if (value.startsWith('ukpil-')) {
                // Viewing UK MHRA Patient Information Leaflet PDF
                const id = parseInt(value.replace('ukpil-', ''), 10);
                console.log('[VersionSelector] Selecting UK PIL, id:', id);
                onVersionChange(null); // Clear draft selection
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('ukpil', id);
                }
              } else if (value.startsWith('capm-')) {
                // Viewing CA Health Canada Product Monograph PDF
                const id = parseInt(value.replace('capm-', ''), 10);
                console.log('[VersionSelector] Selecting CA PM, id:', id);
                onVersionChange(null); // Clear draft selection
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('capm', id);
                }
              } else if (value.startsWith('audoc-')) {
                // Viewing AU TGA (PI / CMI) document
                const id = parseInt(value.replace('audoc-', ''), 10);
                console.log('[VersionSelector] Selecting AU doc, id:', id);
                onVersionChange(null); // Clear draft selection
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('audoc', id);
                }
              } else if (value.startsWith('eudoc-')) {
                // Viewing EU EMA SmPC document
                const id = parseInt(value.replace('eudoc-', ''), 10);
                console.log('[VersionSelector] Selecting EU doc, id:', id);
                onVersionChange(null); // Clear draft selection
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('eudoc', id);
                }
              } else if (value.startsWith('jppdfdoc-')) {
                // Viewing JP PMDA supplementary PDF
                const id = parseInt(value.replace('jppdfdoc-', ''), 10);
                console.log('[VersionSelector] Selecting JP PDF doc, id:', id);
                onVersionChange(null); // Clear draft selection
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('jppdfdoc', id);
                }
              } else if (value.startsWith('chpdfdoc-')) {
                // Viewing a Swissmedic label PDF (Fachinformation / Patienteninformation)
                const id = parseInt(value.replace('chpdfdoc-', ''), 10);
                onVersionChange(null);
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('chpdfdoc', id);
                }
              } else if (value.startsWith('frhtmldoc-')) {
                // Viewing an ANSM label document (RCP / Notice)
                const id = parseInt(value.replace('frhtmldoc-', ''), 10);
                onVersionChange(null);
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('frhtmldoc', id);
                }
              } else if (value.startsWith('fdalabelpdfdoc-')) {
                // Viewing a Drugs@FDA label PDF (Prescribing Information)
                const id = parseInt(value.replace('fdalabelpdfdoc-', ''), 10);
                onVersionChange(null);
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('fdalabelpdfdoc', id);
                }
              } else if (value.startsWith('depdfdoc-')) {
                // Viewing a BfArM label PDF (Fachinformation / Gebrauchsinformation)
                const id = parseInt(value.replace('depdfdoc-', ''), 10);
                onVersionChange(null);
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('depdfdoc', id);
                }
              } else if (value.startsWith('zhpdfdoc-')) {
                // Viewing CN NMPA supplementary PDF (admin-only)
                const id = parseInt(value.replace('zhpdfdoc-', ''), 10);
                console.log('[VersionSelector] Selecting CN PDF doc, id:', id);
                onVersionChange(null); // Clear draft selection
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('zhpdfdoc', id);
                }
              } else if (value === 'jplabel') {
                // Back to the JP XML package-insert label
                console.log('[VersionSelector] Selecting JP label');
                onVersionChange(null);
                if (onUnifiedVersionChange) {
                  onUnifiedVersionChange('jplabel', 0);
                }
              }
              // Task B3: draft selection is handled by the dedicated
              // `renderDraftsPicker()` <select> now — this <select>
              // no longer has any `draft-{id}` <option>, so there is
              // deliberately no `draft-` branch here anymore.
            }}
            disabled={isLoading}
            className="appearance-none bg-white border border-slate-200 rounded px-3 py-1.5 pr-8 text-sm font-medium text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {/* Empty placeholder so the controlled value can be '' —
                i.e. the <select> shows NOTHING selected when a draft is
                the active view (drafts live in their own picker, Task
                B3) or when an FDA label is active in the submission
                control (Task B2 orphan guard). Hidden from the list; it
                only backs the '' value so the native <select> doesn't
                fall back to auto-selecting its first option. It is NOT a
                real option, so it does not affect hasAnySelectOption. */}
            <option value="" hidden aria-label="none selected" />
            {/* FDA Versions Group — getVersionLabel renders the
                regional name + DD/MM/YYYY date inline, so no
                separate date suffix is appended here.  Gated on
                showFdaOption so AU + CA drugs (whose canonical
                label IS the per-family PDF, not the parsed HTML
                sections) can suppress the redundant primary entry.
                Task B2 (submission-group-selector v2 plan): ALSO
                gated on !sbaCycleInfo — once the submission control
                owns FDA label navigation, the current + historical
                labels are reached via the cycle card / unmatched
                surface above, not this optgroup. Flag off
                (sbaCycleInfo absent) renders exactly as before. */}
            {!sbaCycleInfo && showFdaOption && sortedFdaVersions.length > 0 && (
              <optgroup label="FDA Versions">
                {sortedFdaVersions.map((version) => (
                  <option
                    key={`${version.source}-${version.id}`}
                    value={`${version.source}-${version.id}`}
                  >
                    {getVersionLabel(version, drugRegion)}
                  </option>
                ))}
              </optgroup>
            )}

            {/* Only show fallback label option if no unified versions.
                Same !sbaCycleInfo gate as the optgroup above. */}
            {!sbaCycleInfo && sortedFdaVersions.length === 0 && showFdaOption && (
              <option value="fda">
                {labelPrefixForRegion(drugRegion)}
              </option>
            )}

            {/* Draft Versions — Task B3 (submission-group-selector-v2
                plan): drafts moved OUT of this <select> into their own
                dedicated `renderDraftsPicker()` dropdown (rendered
                above, flag-independent). No optgroup here anymore. */}

            {/* SBA (FDA Reviews) — grouped by application so a multi-NDA
                drug gets one sub-header per approval package. The
                backend (get_bundle_documents) returns docs in Phase 9b
                priority order (medical_review first); Map preserves
                insertion order so within each group the priority is
                retained. `doc_type_label` is always a string
                (bundle_projection_service.doc_type_label coerces null
                doc_type to "Other Review") — no client fallback needed.
                Gated on !sbaCycleInfo (Task 3, linked split-dropdowns):
                once the envelope is present, EVERY sba doc — classified
                or unclassified — is reachable via the HA Review
                split-dropdown (`buildDocGroups` covers all of
                `sbaDocuments` unconditionally); rendering them here too
                would duplicate them and leave a selectable option the
                split-dropdown's active-item highlight can't see. Flag
                off (sbaCycleInfo absent) keeps this block exactly as it
                always was. */}
            {!sbaCycleInfo && classicSbaDocuments.length > 0 && (() => {
              // Group by application_number alone so sibling reviews from
              // the same submission stay in one bucket even when only some
              // PDFs had application_type successfully extracted. Type for
              // the header is resolved via sibling propagation, then
              // number-range inference (see sbaApplicationType.ts).
              const groups = new Map<string, typeof classicSbaDocuments>();
              for (const doc of classicSbaDocuments) {
                const key = doc.application_number || '';
                const bucket = groups.get(key);
                if (bucket) bucket.push(doc);
                else groups.set(key, [doc]);
              }
              return Array.from(groups.entries()).map(([appNumber, docs]) => {
                const appType = resolveSbaApplicationType(appNumber, classicSbaDocuments);
                const appKey = appNumber
                  ? `${appType ? appType + ' ' : ''}${appNumber}`
                  : '';
                return (
                  <optgroup
                    key={`sba-group-${appKey || 'unknown'}`}
                    label={appKey ? `FDA Reviews — ${appKey}` : 'FDA Reviews'}
                  >
                    {docs.map((doc) => (
                      <option
                        key={`sba-${doc.id}`}
                        value={`sba-${doc.id}`}
                      >
                        {doc.doc_type_label}{multiSubmissionSuffix(doc.is_multi_submission)}
                        {isPinnedSbaDocument(doc.id) ? ' (Search result)' : ''}
                      </option>
                    ))}
                  </optgroup>
                );
              });
            })()}

            {/* EPAR (EMA Reviews) — same group-by-application pattern as
                SBA above.  EPARs typically have one application per
                drug so the grouping is degenerate, but the structure
                stays consistent so a multi-application drug renders
                cleanly on both sides. */}
            {eparDocuments && eparDocuments.length > 0 && (() => {
              const groups = new Map<string, typeof eparDocuments>();
              for (const doc of eparDocuments) {
                const appKey = doc.application_number
                  ? `${doc.application_type || 'EU'} ${doc.application_number}`
                  : '';
                const bucket = groups.get(appKey);
                if (bucket) bucket.push(doc);
                else groups.set(appKey, [doc]);
              }
              return Array.from(groups.entries()).map(([appKey, docs]) => (
                <optgroup
                  key={`epar-group-${appKey || 'unknown'}`}
                  label={appKey ? `EMA Reviews — ${appKey}` : 'EMA Reviews'}
                >
                  {docs.map((doc) => (
                    <option
                      key={`epar-${doc.id}`}
                      value={`epar-${doc.id}`}
                    >
                      {doc.doc_type_label}
                    </option>
                  ))}
                </optgroup>
              ));
            })()}

            {/* UK MHRA PIL PDFs — single group; UK PIL PDFs don't have
                an application_number to group on (the source-native
                identifier is the MHRA `pmid`, which is per-drug, not
                per-application).  All linked PILs render as one group.
                Label distinguishes the parse path ("Gemini fallback"
                or "deterministic") via the audit marker we set in
                last_revised_source — useful for QA. */}
            {ukPilDocuments && ukPilDocuments.filter((d) => !(d.doc_type ?? '').startsWith('nice_')).length > 0 && (
              <optgroup label="UK MHRA — Patient Information Leaflet">
                {ukPilDocuments.filter((d) => !(d.doc_type ?? '').startsWith('nice_')).map((doc) => {
                  // `parse_method` is exposed on the API response but
                  // not on the SBASummaryForDrug type yet; safe-cast.
                  const parseMethod = (doc as unknown as { parse_method?: string }).parse_method;
                  const suffix = parseMethod === 'gemini_fallback'
                    ? ' (Gemini fallback)'
                    : '';
                  return (
                    <option
                      key={`ukpil-${doc.id}`}
                      value={`ukpil-${doc.id}`}
                    >
                      {doc.doc_type_label}{suffix}
                    </option>
                  );
                })}
              </optgroup>
            )}

            {/* NICE HTA (Commercial / HTA Assessments) — supplementary appraisal
                docs about the UK drug's NICE technology appraisal.  They ride the
                UK PIL family (same uk_pil_* tables / ukpil- select value) but are
                grouped separately by their nice_* doc_type. */}
            {ukPilDocuments && ukPilDocuments.filter((d) => (d.doc_type ?? '').startsWith('nice_')).length > 0 && (
              <optgroup label="NICE HTA — Appraisal & Committee Documents">
                {ukPilDocuments.filter((d) => (d.doc_type ?? '').startsWith('nice_')).map((doc) => (
                  <option key={`ukpil-${doc.id}`} value={`ukpil-${doc.id}`}>
                    {doc.doc_type_label}
                  </option>
                ))}
              </optgroup>
            )}

            {/* CA Health Canada PM PDFs — single group; like UK PIL,
                CA PM PDFs don't have an application_number to group on
                (the source-native identifier is the `pm_code`, which
                is per-drug, not per-application).  All linked Product
                Monographs render as one group. */}
            {caPmDocuments && caPmDocuments.length > 0 && (
              <optgroup label="Health Canada — Product Monograph">
                {caPmDocuments.map((doc) => (
                  <option
                    key={`capm-${doc.id}`}
                    value={`capm-${doc.id}`}
                  >
                    {doc.doc_type_label}
                  </option>
                ))}
              </optgroup>
            )}

            {/* AU TGA (PI / CMI) documents — single group; like UK PIL
                and CA PM, AU documents don't have an application_number
                to group on.  AU is a two-doc-type family — the backend's
                `doc_type_label` already carries the per-type display
                string (PI vs CMI), so each option renders it directly. */}
            {auDocuments && auDocuments.length > 0 && (
              <optgroup label="Australia — TGA (PI / CMI)">
                {auDocuments.map((doc) => (
                  <option
                    key={`audoc-${doc.id}`}
                    value={`audoc-${doc.id}`}
                  >
                    {doc.doc_type_label}
                  </option>
                ))}
              </optgroup>
            )}

            {/* EU EMA SmPC documents — single group; single-doc-type
                family ("smpc").  The backend's `doc_type_label` carries
                "EU SmPC", rendered directly per option. */}
            {euDocuments && euDocuments.length > 0 && (
              <optgroup label="EU — EMA SmPC">
                {euDocuments.map((doc) => (
                  <option
                    key={`eudoc-${doc.id}`}
                    value={`eudoc-${doc.id}`}
                  >
                    {doc.doc_type_label}
                  </option>
                ))}
              </optgroup>
            )}

            {/* JP package insert (XML label) — the primary/default view
                for JP drugs; selecting it returns from a supplementary PDF. */}
            {drugRegion === 'JP' && (
              <option value="jplabel">Package Insert (添付文書)</option>
            )}

            {/* JP PMDA supplementary PDFs — single group; multi-doc-type
                family (patient leaflet / interview form / review report /
                RMP), so each option's `doc_type_label` distinguishes it. */}
            {jpPdfDocuments && jpPdfDocuments.length > 0 && (
              <optgroup label="Japan — PMDA Supplementary Documents">
                {jpPdfDocuments.map((doc) => (
                  <option
                    key={`jppdfdoc-${doc.id}`}
                    value={`jppdfdoc-${doc.id}`}
                  >
                    {doc.doc_type_label}
                  </option>
                ))}
              </optgroup>
            )}

            {/* CN NMPA docs (admin-only; DrugDetailPage passes an empty list to
                non-admins). The Package Insert (说明书) is the MAIN label — it is
                surfaced as a primary top-level option like JP's jplabel, NOT as a
                supplementary doc. Only the other doc types (e.g. the Review Report
                技术审评报告) sit under "Supplementary Documents". */}
            {zhPdfDocuments && zhPdfDocuments.length > 0 && (() => {
              const zhMain = zhPdfDocuments.find(
                (d) => d.doc_type === 'package_insert',
              );
              const zhSupp = zhPdfDocuments.filter(
                (d) => d.doc_type !== 'package_insert',
              );
              return (
                <>
                  {zhMain && (
                    <option value={`zhpdfdoc-${zhMain.id}`}>
                      Package Insert (说明书)
                    </option>
                  )}
                  {zhSupp.length > 0 && (
                    <optgroup label="China — NMPA Supplementary Documents">
                      {zhSupp.map((doc) => (
                        <option
                          key={`zhpdfdoc-${doc.id}`}
                          value={`zhpdfdoc-${doc.id}`}
                        >
                          {doc.doc_type_label}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </>
              );
            })()}

            {/* Swiss labels: Fachinformation (smpc) is the MAIN label, the
                Patienteninformation (pil) sits beside it — both primary options. */}
            {chPdfDocuments && chPdfDocuments.length > 0 && (
              <optgroup label="Switzerland — Swissmedic">
                {[...chPdfDocuments]
                  .sort((a, b) => (a.doc_type === 'smpc' ? -1 : 0) - (b.doc_type === 'smpc' ? -1 : 0))
                  .map((doc) => (
                    <option key={`chpdfdoc-${doc.id}`} value={`chpdfdoc-${doc.id}`}>
                      {doc.doc_type_label}
                    </option>
                  ))}
              </optgroup>
            )}

            {/* US Drugs@FDA label PDFs: EVERY approved labeling action, newest
                first. Unlike the other families this group is a version
                history, not a doc-type list — Humira has 41 — so each option
                is identified by its FDA version string and action date, which
                is what a reviewer recognises. */}
            {fdaLabelPdfDocuments && fdaLabelPdfDocuments.length > 0 && (
              <optgroup label="United States — Drugs@FDA label PDF">
                {[...fdaLabelPdfDocuments]
                  .sort((a, b) => String(b.action_date ?? '').localeCompare(
                    String(a.action_date ?? '')))
                  .map((doc) => (
                    <option key={`fdalabelpdfdoc-${doc.id}`}
                            value={`fdalabelpdfdoc-${doc.id}`}>
                      {doc.version_str || doc.doc_type_label}
                      {doc.action_date ? ` · ${doc.action_date}` : ''}
                    </option>
                  ))}
              </optgroup>
            )}

            {/* German labels: Fachinformation (smpc) first — the MAIN label —
                then the Gebrauchsinformation (pil), each with its version date. */}
            {/* German labels: Fachinformation (smpc) first, then the
                Gebrauchsinformation (pil), each with its version date. */}
            {dePdfDocuments && dePdfDocuments.length > 0 && (dePdfDocuments[0]?.family ?? 'de_pdf') === 'de_pdf' && (
              <optgroup label="Germany — BfArM">
                {[...dePdfDocuments]
                  .sort((a, b) => (a.doc_type === 'smpc' ? -1 : 0) - (b.doc_type === 'smpc' ? -1 : 0))
                  .map((doc) => (
                    <option key={`depdfdoc-${doc.id}`} value={`depdfdoc-${doc.id}`}>
                      {doc.doc_type_label}
                      {doc.version_str ? ` · ${doc.version_str.replace(/-rev\d+$/, '')}` : ''}
                    </option>
                  ))}
              </optgroup>
            )}

            {/* Irish / Italian labels (France's layout): SmPCs are top-level
                options, the leaflets are supplementary. An AIFA bundle has one
                SmPC per strength/form (TACHIPIRINA: 5) — none of them is "the"
                main label, so the tag only shows when there is exactly one. */}
            {dePdfDocuments && dePdfDocuments.length > 0 && (dePdfDocuments[0]?.family ?? 'de_pdf') !== 'de_pdf' && (() => {
              const opt = (doc: (typeof dePdfDocuments)[number], main: boolean) => (
                <option key={`depdfdoc-${doc.id}`} value={`depdfdoc-${doc.id}`}>
                  {doc.doc_type_label}
                  {doc.part_title ? ` · ${doc.part_title}` : ''}
                  {main ? ' (Main label)' : ''}
                </option>
              );
              const region = dePdfDocuments[0]?.family === 'ie_pdf' ? 'Ireland — HPRA' : 'Italy — AIFA';
              const supp = dePdfDocuments.filter((d) => d.doc_type !== 'smpc');
              return (
                <>
                  {dePdfDocuments.filter((d) => d.doc_type === 'smpc').map((d, _i, smpcs) => opt(d, smpcs.length === 1))}
                  {supp.length > 0 && (
                    <optgroup label={`${region} Supplementary Documents`}>
                      {supp.map((d) => opt(d, false))}
                    </optgroup>
                  )}
                </>
              );
            })()}

            {/* French labels: the RCP is the MAIN label (top-level option, like
                CN's package insert); the Notice is supplementary. */}
            {frHtmlDocuments && frHtmlDocuments.length > 0 && (
              <>
                {frHtmlDocuments.filter((d) => d.doc_type === 'smpc').map((doc) => (
                  <option key={`frhtmldoc-${doc.id}`} value={`frhtmldoc-${doc.id}`}>
                    {doc.doc_type_label} (Main label)
                  </option>
                ))}
                {frHtmlDocuments.some((d) => d.doc_type !== 'smpc') && (
                  <optgroup label="France — ANSM Supplementary Documents">
                    {frHtmlDocuments.filter((d) => d.doc_type !== 'smpc').map((doc) => (
                      <option key={`frhtmldoc-${doc.id}`} value={`frhtmldoc-${doc.id}`}>
                        {doc.doc_type_label} (Supplementary)
                      </option>
                    ))}
                  </optgroup>
                )}
              </>
            )}

            {/* FDA Letters Group - only if there are linked FDA letters.
                Gated on !sbaCycleInfo for the same reason as the FDA-version
                and SBA optgroups above: in submission view the Enforcement
                split-dropdown owns these, and listing them here too would
                offer the same letter from two controls. With the envelope
                absent this renders exactly as before. */}
            {!sbaCycleInfo && fdaLetters && fdaLetters.length > 0 && (
              <optgroup label="FDA Enforcement Letters">
                {fdaLetters.map((letter) => (
                  <option
                    key={`fda-letter-${letter.id}`}
                    value={`fda-letter-${letter.id}`}
                  >
                    {fdaLetterTypeLabel(letter.letter_type)}
                    {letter.issued_date ? ` — ${letter.issued_date}` : ''}
                    {fdaLetterRoleSuffix(letter.role)}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg
              className="h-4 w-4 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
        )}
      </div>
    );
  }

  // Legacy fallback: use the old versions array (drafts only).
  // We compute the same value/onChange shape as the unified branch
  // above so SBA / EPAR selection works on EU-only drugs too — those
  // pages don't have ``unifiedVersions`` populated (no FDA history)
  // but they DO have linked EPARs that need to appear in the dropdown.

  const fallbackComputeValue = (): string => {
    if (sbaCycleInfo && selectedApprovalLetterId != null) {
      return '';
    }
    // Deliberately unguarded — see the unified computeValue's matching branch
    // above for why an `sbaCycleInfo ? '' : …` guard here would be dead code.
    if (selectedFdaLetterId !== null && selectedFdaLetterId !== undefined) {
      return `fda-letter-${selectedFdaLetterId}`;
    }
    // Skip when the review-cycle envelope is present — see the unified
    // computeValue's matching branch above for the rationale (every sba
    // doc is reachable via the HA Review split-dropdown instead).
    if (!sbaCycleInfo && selectedSbaId !== null && selectedSbaId !== undefined) {
      return `sba-${selectedSbaId}`;
    }
    if (selectedEparId !== null && selectedEparId !== undefined) {
      return `epar-${selectedEparId}`;
    }
    if (selectedUkPilId !== null && selectedUkPilId !== undefined) {
      return `ukpil-${selectedUkPilId}`;
    }
    if (selectedCaPmId !== null && selectedCaPmId !== undefined) {
      return `capm-${selectedCaPmId}`;
    }
    if (selectedAuId !== null && selectedAuId !== undefined) {
      return `audoc-${selectedAuId}`;
    }
    if (selectedEuId !== null && selectedEuId !== undefined) {
      return `eudoc-${selectedEuId}`;
    }
    // NOTE: drafts are no longer an option in this <select> (Task B3
    // — they moved to their own dedicated picker via
    // `renderDraftsPicker`), so there is deliberately no
    // `draft-${selectedVersionId}` branch here anymore — see the
    // matching note on the unified `computeValue` above.
    // Task B3 regression guard — see the matching comment on the
    // unified `computeValue` above: a draft-active state must return
    // '' regardless of `sbaCycleInfo`, so this <select> doesn't falsely
    // highlight "Current Label" while the drafts picker owns the
    // active draft's selection.
    if (selectedVersionId !== null) {
      return '';
    }
    // Task B2 orphan-value guard — see the unified computeValue's
    // matching comment above. When sbaCycleInfo is present, the "fda"
    // current-label option below is gated off too (the submission
    // control owns FDA label navigation).
    if (!sbaCycleInfo) {
      return 'fda';
    }
    return '';
  };

  // Task B2 — same "hide the select entirely when it has zero
  // remaining options" guard as the unified branch above, adapted to
  // this branch's option set (no EU/JP groups here — see the render
  // block below). Task B3: drafts are intentionally EXCLUDED from
  // this option-presence check — see the matching note on the
  // unified branch's `hasAnySelectOption` above.
  const fallbackHasFdaOption = !sbaCycleInfo && showFdaOption;
  const fallbackHasSbaOptions = !sbaCycleInfo && classicSbaDocuments.length > 0;
  const fallbackHasEparOptions = !!eparDocuments && eparDocuments.length > 0;
  const fallbackHasUkPilOptions = !!ukPilDocuments && ukPilDocuments.length > 0;
  const fallbackHasCaPmOptions = !!caPmDocuments && caPmDocuments.length > 0;
  const fallbackHasAuOptions = !!auDocuments && auDocuments.length > 0;
  const fallbackHasFdaLetterOptions = !sbaCycleInfo && !!fdaLetters && fdaLetters.length > 0;
  const fallbackHasAnySelectOption =
    fallbackHasFdaOption ||
    fallbackHasSbaOptions ||
    fallbackHasEparOptions ||
    fallbackHasUkPilOptions ||
    fallbackHasCaPmOptions ||
    fallbackHasAuOptions ||
    fallbackHasFdaLetterOptions;

  return (
    // Task B5: same flex-wrap treatment as the unified render path
    // above, kept symmetric — see the comment there.
    <div className="relative flex flex-wrap items-start gap-2">
      {renderSubmissionSplitControls()}
      {renderSubmissionViewToggle()}
      {renderDraftsPicker(
        versions.map((version) => ({
          id: version.id,
          label:
            `Draft v${version.version_number}` +
            (version.status !== 'published' ? ` (${version.status})` : '') +
            (version.comment_count > 0 ? ` - ${version.comment_count} comments` : ''),
        })),
      )}
      {fallbackHasAnySelectOption && (
      <div className="relative">
        <select
          value={fallbackComputeValue()}
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'fda') {
              onVersionChange(null);
              if (onUnifiedVersionChange) {
                onUnifiedVersionChange('fda_current', 0);
              }
            } else if (value.startsWith('sba-')) {
              const id = parseInt(value.replace('sba-', ''), 10);
              onVersionChange(null);
              if (onUnifiedVersionChange) {
                onUnifiedVersionChange('sba', id);
              }
            } else if (value.startsWith('epar-')) {
              const id = parseInt(value.replace('epar-', ''), 10);
              onVersionChange(null);
              if (onUnifiedVersionChange) {
                onUnifiedVersionChange('epar', id);
              }
            } else if (value.startsWith('ukpil-')) {
              const id = parseInt(value.replace('ukpil-', ''), 10);
              onVersionChange(null);
              if (onUnifiedVersionChange) {
                onUnifiedVersionChange('ukpil', id);
              }
            } else if (value.startsWith('capm-')) {
              const id = parseInt(value.replace('capm-', ''), 10);
              onVersionChange(null);
              if (onUnifiedVersionChange) {
                onUnifiedVersionChange('capm', id);
              }
            } else if (value.startsWith('audoc-')) {
              const id = parseInt(value.replace('audoc-', ''), 10);
              onVersionChange(null);
              if (onUnifiedVersionChange) {
                onUnifiedVersionChange('audoc', id);
              }
            } else if (value.startsWith('fda-letter-')) {
              const id = parseInt(value.replace('fda-letter-', ''), 10);
              onVersionChange(null);
              if (onUnifiedVersionChange) {
                onUnifiedVersionChange('fda-letter', id);
              }
            } else {
              // Legacy bare-id format from old call sites — treat as draft.
              // NOTE: Task B3 removed the `draft-{id}` <option>s from this
              // <select> (drafts now live in `renderDraftsPicker()`), so
              // there is deliberately no `draft-` prefix branch here
              // anymore — see the matching note on the unified onChange
              // above.
              onVersionChange(parseInt(value));
            }
          }}
          disabled={isLoading}
          className="appearance-none bg-white border border-slate-200 rounded px-3 py-1.5 pr-8 text-sm font-medium text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {/* Empty placeholder backing the '' value (draft active /
              Task B2 orphan guard) so this <select> shows nothing
              selected instead of auto-selecting its first option.
              Hidden; not a real option, doesn't affect hide-empty. */}
          <option value="" hidden aria-label="none selected" />
          {/* Current label option - always first.  Region-aware
              naming via ``labelPrefixForRegion`` (USPI / EU SmPC /
              UK SmPC / CA Product Monograph / AU PI).  Task B2: gated
              on !sbaCycleInfo — once the submission control owns FDA
              label navigation, the current label is reached via the
              cycle card / unmatched surface above, not this option. */}
          {!sbaCycleInfo && showFdaOption && (
            <option value="fda">
              {labelPrefixForRegion(drugRegion)}
            </option>
          )}

          {/* Draft versions — Task B3: moved OUT of this <select> into
              the dedicated `renderDraftsPicker()` dropdown above
              (flag-independent). The separator that used to sit
              between the "fda" option and the draft options is gone
              too — nothing to separate from once drafts left. */}

          {/* SBA (FDA Reviews) — same group-by-application as the unified
              branch above. Gated on !sbaCycleInfo — see the matching
              comment on the unified branch's SBA optgroup above
              (Task 3, linked split-dropdowns). */}
          {!sbaCycleInfo && classicSbaDocuments.length > 0 && (() => {
            const groups = new Map<string, typeof classicSbaDocuments>();
            for (const doc of classicSbaDocuments) {
              const key = doc.application_number || '';
              const bucket = groups.get(key);
              if (bucket) bucket.push(doc);
              else groups.set(key, [doc]);
            }
            return Array.from(groups.entries()).map(([appNumber, docs]) => {
              const appType = resolveSbaApplicationType(appNumber, classicSbaDocuments);
              const appKey = appNumber
                ? `${appType ? appType + ' ' : ''}${appNumber}`
                : '';
              return (
                <optgroup
                  key={`sba-group-${appKey || 'unknown'}`}
                  label={appKey ? `FDA Reviews — ${appKey}` : 'FDA Reviews'}
                >
                  {docs.map((doc) => (
                    <option key={`sba-${doc.id}`} value={`sba-${doc.id}`}>
                      {doc.doc_type_label}{multiSubmissionSuffix(doc.is_multi_submission)}
                      {isPinnedSbaDocument(doc.id) ? ' (Search result)' : ''}
                    </option>
                  ))}
                </optgroup>
              );
            });
          })()}

          {/* EPAR (EMA Reviews) */}
          {eparDocuments && eparDocuments.length > 0 && (() => {
            const groups = new Map<string, typeof eparDocuments>();
            for (const doc of eparDocuments) {
              const appKey = doc.application_number
                ? `${doc.application_type || 'EU'} ${doc.application_number}`
                : '';
              const bucket = groups.get(appKey);
              if (bucket) bucket.push(doc);
              else groups.set(appKey, [doc]);
            }
            return Array.from(groups.entries()).map(([appKey, docs]) => (
              <optgroup
                key={`epar-group-${appKey || 'unknown'}`}
                label={appKey ? `EMA Reviews — ${appKey}` : 'EMA Reviews'}
              >
                {docs.map((doc) => (
                  <option key={`epar-${doc.id}`} value={`epar-${doc.id}`}>
                    {doc.doc_type_label}
                  </option>
                ))}
              </optgroup>
            ));
          })()}

          {/* UK MHRA PIL PDFs (legacy fallback render) — same shape as
              the unified branch above. */}
          {ukPilDocuments && ukPilDocuments.length > 0 && (
            <optgroup label="UK MHRA — Patient Information Leaflet">
              {ukPilDocuments.map((doc) => {
                const parseMethod = (doc as unknown as { parse_method?: string }).parse_method;
                const suffix = parseMethod === 'gemini_fallback'
                  ? ' (Gemini fallback)'
                  : '';
                return (
                  <option key={`ukpil-${doc.id}`} value={`ukpil-${doc.id}`}>
                    {doc.doc_type_label}{suffix}
                  </option>
                );
              })}
            </optgroup>
          )}

          {/* CA Health Canada PM PDFs (legacy fallback render) — same
              shape as the unified branch above. */}
          {caPmDocuments && caPmDocuments.length > 0 && (
            <optgroup label="Health Canada — Product Monograph">
              {caPmDocuments.map((doc) => (
                <option key={`capm-${doc.id}`} value={`capm-${doc.id}`}>
                  {doc.doc_type_label}
                </option>
              ))}
            </optgroup>
          )}

          {/* AU TGA (PI / CMI) documents (legacy fallback render) —
              same shape as the unified branch above. */}
          {auDocuments && auDocuments.length > 0 && (
            <optgroup label="Australia — TGA (PI / CMI)">
              {auDocuments.map((doc) => (
                <option key={`audoc-${doc.id}`} value={`audoc-${doc.id}`}>
                  {doc.doc_type_label}
                </option>
              ))}
            </optgroup>
          )}

          {/* FDA Letters — same !sbaCycleInfo gate as the unified branch
              above; see the comment there. */}
          {!sbaCycleInfo && fdaLetters && fdaLetters.length > 0 && (
            <optgroup label="FDA Enforcement Letters">
              {fdaLetters.map((letter) => (
                <option
                  key={`fda-letter-${letter.id}`}
                  value={`fda-letter-${letter.id}`}
                >
                  {fdaLetterTypeLabel(letter.letter_type)}
                  {letter.issued_date ? ` — ${letter.issued_date}` : ''}
                  {fdaLetterRoleSuffix(letter.role)}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className="h-4 w-4 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
      )}
    </div>
  );
};

export default VersionSelector;
